import { useMemo } from 'react';
import { addDays, format } from 'date-fns';
import { isSelectableStartDate, MIN_CORRECTION_LEAD_DAYS } from '../../../utils/vacationStartDateRules';
import { computeEndDate } from '../../../utils/vacationRules';
import { createDateSafe } from '@/utils/dateUtils';
import type { VacationSegment, VacationRequest } from '../../../models/vacation.types';
import { resolveSegmentDecision } from '../../../utils/vacationSegments';

const DEFAULT_MONTH_SEARCH_DAYS = 400;

interface UseCorrectionDateBoundsParams {
    request: VacationRequest;
    segment: VacationSegment;
    holidays: string[];
}

interface UseCorrectionDateBoundsResult {
    isDateSelectable: (isoDate: string) => boolean;
    defaultMonthIso: string;
    minDateIso: string;
}

export function useCorrectionDateBounds({
    request,
    segment,
    holidays,
}: UseCorrectionDateBoundsParams): UseCorrectionDateBoundsResult {
    const { requestedAt, segments, concessiveDeadline } = request;

    return useMemo(() => {
        const today = new Date();
        const requestDate = requestedAt?.toDate?.() ?? today;
        const minDateIso = format(addDays(today, MIN_CORRECTION_LEAD_DAYS), 'yyyy-MM-dd');
        const otherSegments = segments.filter((other) => other.order !== segment.order);
        const rejection = resolveSegmentDecision(request, segment.order);
        const rejectedStart = rejection?.status === 'Rejected' ? rejection.startDate ?? null : null;

        const isDateSelectable = (isoDate: string): boolean => {
            if (!isSelectableStartDate(isoDate, requestDate, minDateIso, holidays, concessiveDeadline)) return false;
            if (rejectedStart && isoDate === rejectedStart) return false;

            const proposedEnd = computeEndDate(isoDate, segment.days);
            return otherSegments.every(
                (other) => proposedEnd < other.startDate || isoDate > other.endDate
            );
        };

        const start = createDateSafe(minDateIso) ?? today;
        let defaultMonthIso = minDateIso;
        for (let offset = 0; offset < DEFAULT_MONTH_SEARCH_DAYS; offset++) {
            const candidate = format(addDays(start, offset), 'yyyy-MM-dd');
            if (isDateSelectable(candidate)) {
                defaultMonthIso = candidate;
                break;
            }
        }

        return { isDateSelectable, defaultMonthIso, minDateIso };
    }, [request, requestedAt, segments, segment.order, segment.days, holidays, concessiveDeadline]);
}
